import { LoadingBlock } from "../UI/Loading/LoadingRows.tsx";
import * as yup from "yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { DateTimeInput, Select } from "@normasteaching/norma-ui";
import dayjs from "dayjs";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";
import { TextInput } from "../UI/TextInput.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { useAllFranchisees } from "../Franchisee/useAllFranchisees.ts";
import { useAllUsers } from "../User/useAllUsers.ts";
import { useState, useEffect } from "react";
import { DebouncedTextInput } from "../UI/DebouncedTextInput.tsx";
import { WeekSlotInput } from "./WeekSlotInput.tsx";
import { SpeakingLevel } from "../gql/graphql.ts";
import { useGetUser1on1ClassroomPlansToBeRedeemed } from "./useGetUser1on1ClassroomPlansToBeRedeemed.ts";
import { useGetActiveIntentsForStudent } from "./useGetActiveIntentsForStudent.ts";

export type CreateBulkClassRoomFormInputs = {
  teacherId: string;
  studentId: string;
  startDate: Date;
  numberOfWeeks: number;
  maxClassRoomNumber: number;
  user1on1ClassroomPlanToBeRedeemedId: string;
  isGiftedPlan: boolean;
  weekSlots: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  }>;
};

const GIFTED_PLAN_VALUE = "GIFTED_PLAN";

const schema = yup
  .object({
    teacherId: yup.string().required("Seleziona un insegnante"),
    studentId: yup.string().required("Seleziona un utente"),
    user1on1ClassroomPlanToBeRedeemedId: yup
      .string()
      .required("Seleziona un piano 1on1 o 'Piano personalizzato'"),
    isGiftedPlan: yup.boolean().required(),
    startDate: yup.date().required("Specifica la data di inizio"),
    numberOfWeeks: yup
      .number()
      .typeError("Specifica il numero di settimane")
      .required("Specifica il numero di settimane")
      .min(1, "Il numero minimo di settimane è 1"),
    maxClassRoomNumber: yup
      .number()
      .typeError("Specifica il numero massimo di classi")
      .required("Specifica il numero massimo di classi")
      .min(1, "Il numero minimo di classi è 1"),
    weekSlots: yup
      .array()
      .required()
      .min(1, "Seleziona almeno uno slot settimanale"),
  })
  .required();

export function CreateBulkClassRoomForm({
  onCreate,
}: {
  onCreate: (data: CreateBulkClassRoomFormInputs) => Promise<void>;
}) {
  function getDefaultStartDate() {
    return dayjs().add(1, "day").startOf("day").hour(7).toDate();
  }

  const [userEmailFilter, setUserEmailFilter] = useState<string>("");

  const { franchisees, loading: loadingFranchisees } = useAllFranchisees({
    isOneOnOne: true,
  });
  const { users, loading: loadingUsers } = useAllUsers({
    limit: 20,
    email: userEmailFilter || undefined,
    skip: !userEmailFilter,
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm<CreateBulkClassRoomFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      startDate: getDefaultStartDate(),
      numberOfWeeks: 1,
      weekSlots: [],
      isGiftedPlan: false,
    },
  });

  const selectedStudentId = watch("studentId");
  const selectedPlanId = watch("user1on1ClassroomPlanToBeRedeemedId");
  const isGiftedPlan = watch("isGiftedPlan");
  const weekSlots = watch("weekSlots");
  const numberOfWeeks = watch("numberOfWeeks");
  const maxClassRoomNumber = watch("maxClassRoomNumber");

  const { plans, loading: loadingPlans } =
    useGetUser1on1ClassroomPlansToBeRedeemed(selectedStudentId ?? "");

  const { activeIntents } = useGetActiveIntentsForStudent(
    selectedStudentId ?? "",
  );

  const takenPlanIds = new Set(
    activeIntents
      .map((i) => i.user1on1ClassroomPlanToBeRedeemedId)
      .filter(Boolean),
  );
  const hasGiftedIntent = activeIntents.some(
    (i) => !i.user1on1ClassroomPlanToBeRedeemedId,
  );

  const { withLoading, loading } = useWithLoading();

  // Gestisci il cambio tra piano regalato e piano normale
  useEffect(() => {
    if (selectedPlanId === GIFTED_PLAN_VALUE) {
      setValue("isGiftedPlan", true);
    } else {
      setValue("isGiftedPlan", false);
    }
  }, [selectedPlanId, setValue]);

  // Aggiorna automaticamente maxClassRoomNumber quando viene selezionato un piano
  useEffect(() => {
    if (
      selectedPlanId &&
      selectedPlanId !== GIFTED_PLAN_VALUE &&
      plans.length > 0
    ) {
      const selectedPlan = plans.find((p) => p.id === selectedPlanId);
      if (selectedPlan) {
        setValue("maxClassRoomNumber", selectedPlan.totalNumberOfLessons);
      }
    }
  }, [selectedPlanId, plans, setValue]);

  const selectedStudent = users.find((u) => u.id === selectedStudentId);
  const isStudentLevelValid =
    !selectedStudentId ||
    (selectedStudent &&
      selectedStudent.speakingLevel !== SpeakingLevel.Unknown);

  const hasValidPlanSelection =
    selectedPlanId === GIFTED_PLAN_VALUE ||
    (selectedStudentId && !loadingPlans && plans.length > 0);
  const canSubmit =
    isStudentLevelValid && (hasValidPlanSelection || !selectedStudentId);

  const expectedSlots = (weekSlots?.length ?? 0) * (numberOfWeeks ?? 0);
  const showSlotWarning =
    weekSlots?.length > 0 &&
    numberOfWeeks > 0 &&
    maxClassRoomNumber > 0 &&
    expectedSlots !== Number(maxClassRoomNumber);

  const onSubmit: SubmitHandler<CreateBulkClassRoomFormInputs> = async (
    data,
  ) => {
    await withLoading(async () => onCreate(data));
  };

  return (
    <>
      <div className={"flex flex-col space-y-3"}>
        <InputWithLabel label={"Insegnante"}>
          {loadingFranchisees ? (
            <LoadingBlock sizeClassName={"h-10 w-full"} />
          ) : (
            <Controller
              control={control}
              name={"teacherId"}
              render={({ field }) => {
                return (
                  <Select<string>
                    errorMessage={errors.teacherId?.message}
                    placeholder={"Seleziona un insegnante"}
                    searchable={true}
                    options={franchisees.map((f) => ({
                      name: `${f.givenName} ${f.familyName} (${f.email})`,
                      id: f.id,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                  />
                );
              }}
            />
          )}
        </InputWithLabel>

        <InputWithLabel label={"Utente"}>
          <DebouncedTextInput
            placeholder={"Cerca per email..."}
            onChange={setUserEmailFilter}
            debounceInMs={300}
          />
          <div className="mt-2">
            {loadingUsers ? (
              <LoadingBlock sizeClassName={"h-10 w-full"} />
            ) : (
              <Controller
                control={control}
                name={"studentId"}
                render={({ field }) => {
                  return (
                    <Select<string>
                      errorMessage={errors.studentId?.message}
                      placeholder={"Seleziona un utente"}
                      options={users.map((u) => ({
                        name: `${u.givenName} ${u.familyName} (${u.email})`,
                        id: u.id,
                      }))}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  );
                }}
              />
            )}
          </div>
        </InputWithLabel>

        <InputWithLabel label={"Piano 1on1"}>
          {loadingPlans ? (
            <LoadingBlock sizeClassName={"h-10 w-full"} />
          ) : (
            <Controller
              control={control}
              name={"user1on1ClassroomPlanToBeRedeemedId"}
              render={({ field }) => {
                const planOptions = [
                  ...(!hasGiftedIntent
                    ? [{ name: "Piano personalizzato", id: GIFTED_PLAN_VALUE }]
                    : []),
                  ...plans
                    .filter((p) => !takenPlanIds.has(p.id))
                    .map((p) => ({
                      name: `Piano ${p.id} - ${p.totalNumberOfLessons} lezioni totali`,
                      id: p.id,
                    })),
                ];

                return (
                  <Select<string>
                    errorMessage={
                      errors.user1on1ClassroomPlanToBeRedeemedId?.message
                    }
                    placeholder={
                      "Seleziona un piano 1on1 o 'Piano personalizzato'"
                    }
                    options={planOptions}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!selectedStudentId}
                  />
                );
              }}
            />
          )}
          {!selectedStudentId && (
            <p className="text-sm text-gray-500 mt-1">
              Seleziona prima un utente per vedere i piani disponibili
            </p>
          )}
          {isGiftedPlan && (
            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800">
                ℹ️ Hai selezionato "Piano personalizzato". Puoi procedere anche
                senza che l'utente abbia acquistato un piano.
              </p>
            </div>
          )}
        </InputWithLabel>

        <InputWithLabel label={"Data di inizio piano"}>
          <Controller
            control={control}
            name={"startDate"}
            render={({ field }) => {
              return (
                <DateTimeInput
                  hourFromTo={{ from: 7, to: 22 }}
                  value={field.value}
                  onChange={field.onChange}
                />
              );
            }}
          />
        </InputWithLabel>

        <InputWithLabel label={"Numero di settimane"}>
          <TextInput
            {...register("numberOfWeeks")}
            errorMessage={errors.numberOfWeeks?.message}
            type="number"
            onWheel={() => {
              if (!document.activeElement) return;
              if (
                "blur" in document.activeElement &&
                typeof document.activeElement.blur === "function"
              )
                document.activeElement.blur();
            }}
          />
        </InputWithLabel>

        <InputWithLabel label={"Numero massimo di classi"}>
          <TextInput
            {...register("maxClassRoomNumber")}
            errorMessage={errors.maxClassRoomNumber?.message}
            type="number"
            onWheel={() => {
              if (!document.activeElement) return;
              if (
                "blur" in document.activeElement &&
                typeof document.activeElement.blur === "function"
              )
                document.activeElement.blur();
            }}
          />
        </InputWithLabel>

        <InputWithLabel label={"Slot settimanali (1 ora ciascuno)"}>
          <Controller
            control={control}
            name={"weekSlots"}
            render={({ field }) => {
              return (
                <WeekSlotInput
                  errorMessage={errors.weekSlots?.message}
                  value={field.value}
                  onChange={field.onChange}
                />
              );
            }}
          />
        </InputWithLabel>

        {selectedStudent &&
          selectedStudent.speakingLevel === SpeakingLevel.Unknown && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">
                L'utente selezionato ha un livello di speaking "Unknown".
                Seleziona un utente con un livello valido.
              </p>
            </div>
          )}

        {showSlotWarning && (
          <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-md">
            <p className="text-sm text-yellow-800">
              ⚠️ Attenzione: il numero di slot settimanali ({weekSlots.length}){" "}
              moltiplicato per il numero di settimane ({numberOfWeeks}) =
              <strong> {expectedSlots} slot totali</strong>, ma il numero
              massimo di classi è impostato a{" "}
              <strong>{maxClassRoomNumber}</strong>.
            </p>
          </div>
        )}

        <div className={"flex justify-end"}>
          <div>
            <PrimaryButton
              loading={loading}
              onClick={handleSubmit(onSubmit)}
              label={"Avanti"}
              disabled={!canSubmit}
            />
          </div>
        </div>
      </div>
    </>
  );
}
